/**
 * Composant NotificationBadge - Icône de notification avec badge
 * Intégrée dans le TopNav
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { cn } from "@core/lib/utils";
import { NotificationBadgeState } from "@core/types/notifications";

interface NotificationBadgeProps {
  badgeState: NotificationBadgeState;
  onClick: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  badgeState,
  onClick,
  className = "",
  size = "md",
}) => {
  const { t } = useTranslation("common");
  const sizes = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  const badgeSizes = {
    sm: "w-2 h-2 text-xs",
    md: "w-3 h-3 text-xs",
    lg: "w-4 h-4 text-sm",
  };

  const badgeColors = {
    red: "bg-red-500 text-white border-red-600",
    orange: "bg-orange-500 text-white border-orange-600",
    blue: "bg-blue-500 text-white border-blue-600",
    gray: "bg-gray-500 text-white border-gray-600",
  };

  const iconColors = {
    red: "text-red-400 hover:text-red-300",
    orange: "text-orange-400 hover:text-orange-300",
    blue: "text-blue-400 hover:text-blue-300",
    gray: "text-gray-400 hover:text-gray-300",
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative p-3 md:p-2 rounded-lg transition-all duration-200",
        "hover:bg-gray-800 active:scale-95",
        iconColors[badgeState.color],
        className,
      )}
      aria-label={
        badgeState.count > 0
          ? `${t("notifications_title")} (${t("notifications.badge_unread", { count: badgeState.count })})`
          : t("notifications_title")
      }
    >
      <Bell className={cn(sizes[size], badgeState.pulse && "animate-pulse")} />

      {/* Badge indicateur */}
      {badgeState.visible && (
        <span
          className={cn(
            "absolute -top-1 -right-1 rounded-full flex items-center justify-center font-bold border-2 border-[#1a1a1a]",
            badgeSizes[size],
            badgeColors[badgeState.color],
            badgeState.pulse && "animate-ping",
          )}
        >
          {badgeState.count > 0
            ? badgeState.count > 99
              ? "99+"
              : badgeState.count
            : ""}
        </span>
      )}

      {/* Badge pulse animation */}
      {badgeState.pulse && (
        <span
          className={cn(
            "absolute -top-1 -right-1 rounded-full",
            badgeSizes[size],
            badgeColors[badgeState.color],
            "animate-ping opacity-75",
          )}
        />
      )}
    </button>
  );
};

export default NotificationBadge;
